import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { ArrowLeft, User, FileText, Dumbbell, Calendar, Target, FolderOpen, LogOut, Activity, Clock, CheckCircle, Plus, Trash2, Edit, Save, X, Eye, EyeOff, Copy, ChevronRight, ClipboardCheck, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";
import AssessmentsTab from "./AssessmentsTab";
import ScheduleCalendar from "../components/ScheduleCalendar";
import AvatarUploader from "../components/AvatarUploader";
import { SESSION_PACKAGES, sessionPackageLabel } from "../lib/sessionPackages";

const tabs = [
  { id: "overview", label: "Overview", icon: User },
  // Schedule tab hidden per Otto's request — scheduling is handled via
  // Calendly externally. The ScheduleTab component and /api/sessions endpoints
  // remain in place so this tab can be re-enabled by uncommenting the line.
  // { id: "schedule", label: "Schedule", icon: Calendar },
  { id: "documents", label: "Documents", icon: FolderOpen },
];

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [client, setClient] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [notes, setNotes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [progress, setProgress] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, p, s, m, n, d, a, e, pr] = await Promise.all([
        api.get(`/clients/${clientId}`), api.get(`/programs?client_id=${clientId}`),
        api.get(`/sessions?client_id=${clientId}`), api.get(`/milestones?client_id=${clientId}`),
        api.get(`/notes?client_id=${clientId}`), api.get(`/documents?client_id=${clientId}`),
        api.get(`/assignments?client_id=${clientId}`), api.get("/exercises"),
        api.get(`/progress?client_id=${clientId}`)
      ]);
      setClient(c.data); setPrograms(p.data); setSessions(s.data);
      setMilestones(m.data); setNotes(n.data); setDocuments(d.data);
      setAssignments(a.data); setExercises(e.data); setProgress(pr.data);
    } catch (e) { console.error(e); }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };
  const fmtShort = (d) => { try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return d; } };

  if (!client) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" /></div>;

  const activeProgram = programs.find(p => p.status === "in_progress");

  const renderContent = () => {
    switch (activeTab) {
      case "overview": return <OverviewTab client={client} setClient={setClient} sessions={sessions} fmtDate={fmtDate} fmtShort={fmtShort} navigate={navigate} />;
      case "notes": return <NotesTab clientId={clientId} notes={notes} load={load} fmtDate={fmtDate} />;
      case "assessments": return <AssessmentsTab clientId={clientId} />;
      case "programs": return <ProgramsTab clientId={clientId} programs={programs} assignments={assignments} exercises={exercises} load={load} fmtDate={fmtDate} />;
      case "schedule": return <ScheduleTab clientId={clientId} sessions={sessions} load={load} fmtDate={fmtDate} />;
      case "milestones": return <MilestonesTab clientId={clientId} milestones={milestones} programs={programs} load={load} fmtDate={fmtDate} />;
      case "documents": return <DocumentsTab clientId={clientId} documents={documents} load={load} fmtDate={fmtDate} />;
      default: return null;
    }
  };

  return (
    <div className="flex h-screen bg-background text-foreground" data-testid="client-detail-page">
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transform transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-5 border-b border-border">
          <button onClick={() => navigate("/admin")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-3" data-testid="detail-back-admin">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </button>
          <div className="flex items-center gap-3">
            {client.avatar ? <img src={client.avatar} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 bg-[#007AFF] rounded-full flex items-center justify-center text-white text-sm font-bold">{client.name?.charAt(0)}</div>}
            <div className="min-w-0">
              <p className="font-bold text-sm truncate">{client.name}</p>
              <p className="text-[10px] text-[#007AFF] font-bold uppercase">{client.package}</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setActiveTab(t.id); setSidebarOpen(false); }}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 text-sm transition-colors ${activeTab === t.id ? "bg-[#007AFF] text-white font-bold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
              data-testid={`detail-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <button onClick={logout} className="w-full text-left px-3 py-2.5 flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground"><LogOut className="w-4 h-4" /> Log Out</button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <main className="flex-1 overflow-y-auto min-w-0">
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1"><ChevronRight className="w-5 h-5" /></button>
            <h1 className="text-lg font-bold">{client.name} &mdash; {tabs.find(t => t.id === activeTab)?.label}</h1>
          </div>
        </header>
        <div className="p-6">{renderContent()}</div>
      </main>
    </div>
  );
}

// ====== OVERVIEW TAB ======
function OverviewTab({ client, setClient, sessions, fmtDate, fmtShort, navigate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [deleting, setDeleting] = useState(false);
  const confirm = useConfirm();
  const startEdit = () => { setForm({ name: client.name, email: client.email, phone: client.phone, package: client.package, client_type: client.client_type, client_status: client.client_status || "active", goals: client.goals, bio: client.bio, avatar: client.avatar || "" }); setEditing(true); };
  const saveEdit = async () => {
    await api.put(`/clients/${client.id}`, form);
    const { data } = await api.get(`/clients/${client.id}`);
    setClient(data); setEditing(false);
  };
  const deleteClient = async () => {
    const ok = await confirm({
      title: `Delete ${client.name}?`,
      description: `This permanently removes ${client.name} (${client.email}) and every record tied to them — sessions, programs, assignments, milestones, notes, documents, progress, and assessments. The lead record (if any) stays but is unlinked from this client. This cannot be undone.`,
      confirmText: "Delete client",
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success(`${client.name} has been deleted.`);
      navigate("/admin");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete client.");
      setDeleting(false);
    }
  };
  const upcoming = sessions.filter(s => s.status === "scheduled").slice(0, 3);

  return (
    <div className="space-y-6" data-testid="detail-overview">
      <div className="bg-card border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">Client Information</h3>
          {editing ? (
            <div className="flex gap-2">
              <Button size="sm" onClick={saveEdit} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="detail-save-client"><Save className="w-3 h-3 mr-1" /> Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="rounded-none text-xs"><X className="w-3 h-3 mr-1" /> Cancel</Button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" onClick={startEdit} className="rounded-none text-xs" data-testid="detail-edit-client"><Edit className="w-3 h-3 mr-1" /> Edit</Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={deleteClient}
                disabled={deleting}
                className="rounded-none text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid="detail-delete-client"
              >
                <Trash2 className="w-3 h-3 mr-1" /> {deleting ? "Deleting…" : "Delete"}
              </Button>
            </div>
          )}
        </div>
        {editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs">Photo</Label>
              <AvatarUploader
                value={form.avatar || ""}
                onChange={(dataUrl) => setForm({ ...form, avatar: dataUrl })}
                fallbackName={form.name}
                testIdPrefix="detail-client-avatar"
              />
            </div>
            <div><Label className="text-xs">Name</Label><Input value={form.name || ""} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Email</Label><Input value={form.email || ""} onChange={e => setForm({...form, email: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Phone</Label><Input value={form.phone || ""} onChange={e => setForm({...form, phone: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Package</Label>
              <Select value={form.package} onValueChange={v => setForm({...form, package: v})}><SelectTrigger className="mt-1 rounded-none bg-background border-border text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SESSION_PACKAGES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent></Select></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={form.client_status || "active"} onValueChange={v => setForm({...form, client_status: v})}><SelectTrigger className="mt-1 rounded-none bg-background border-border text-sm" data-testid="detail-client-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="lead">Lead</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="complete">Complete</SelectItem></SelectContent></Select></div>
            <div className="sm:col-span-2"><Label className="text-xs">Goals</Label><Textarea value={form.goals || ""} onChange={e => setForm({...form, goals: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm resize-none" rows={2} /></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div><span className="text-muted-foreground text-xs">Name</span><p className="font-medium">{client.name}</p></div>
            <div><span className="text-muted-foreground text-xs">Email</span><p className="font-medium">{client.email}</p></div>
            <div><span className="text-muted-foreground text-xs">Phone</span><p className="font-medium">{client.phone || "—"}</p></div>
            <div><span className="text-muted-foreground text-xs">Package</span><p><Badge className="bg-[#007AFF]/10 text-[#007AFF] border-0 font-bold uppercase text-[10px]">{sessionPackageLabel(client.package)}</Badge></p></div>
            <div><span className="text-muted-foreground text-xs">Status</span><p data-testid="detail-client-status">
              {(() => {
                const s = (client.client_status || "active").toLowerCase();
                const styles = { lead: "bg-[#007AFF] text-white", active: "bg-[#00CC52]/10 text-[#00CC52] border-0", complete: "bg-secondary text-foreground border-0" };
                return <Badge className={`${styles[s] || ""} font-bold uppercase text-[10px]`}>{s}</Badge>;
              })()}
            </p></div>
            <div><span className="text-muted-foreground text-xs">Type</span><p className="font-medium capitalize">{client.client_type?.replace("_", " ")}</p></div>
            <div><span className="text-muted-foreground text-xs">Since</span><p className="font-medium">{fmtDate(client.created_at)}</p></div>
            <div className="sm:col-span-2 lg:col-span-3"><span className="text-muted-foreground text-xs">Goals</span><p className="font-medium">{client.goals || "—"}</p></div>
          </div>
        )}
      </div>
      <div className="bg-card border border-border p-5">
        <h4 className="font-bold text-sm mb-3">Upcoming Sessions</h4>
        {upcoming.length === 0 ? <p className="text-xs text-muted-foreground">No upcoming sessions</p> : upcoming.map(s => (
          <div key={s.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
            <div><p className="text-sm font-medium capitalize">{s.type?.replace("_", " ")}</p><p className="text-xs text-muted-foreground">{s.notes}</p></div>
            <div className="text-right"><p className="text-sm font-medium">{fmtShort(s.date)}</p><p className="text-xs text-muted-foreground">{s.time}</p></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ====== NOTES TAB ======
function NotesTab({ clientId, notes, load, fmtDate }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", client_visible: false });
  const internal = notes.filter(n => !n.client_visible);
  const visible = notes.filter(n => n.client_visible);

  const save = async () => {
    if (editId) { await api.put(`/notes/${editId}`, { ...form }); }
    else { await api.post("/notes", { client_id: clientId, ...form, type: form.client_visible ? "progress" : "internal" }); }
    setForm({ title: "", content: "", client_visible: false }); setShowForm(false); setEditId(null); load();
  };
  const startEdit = (n) => { setForm({ title: n.title, content: n.content, client_visible: n.client_visible }); setEditId(n.id); setShowForm(true); };
  const del = async (id) => {
    const ok = await confirm({ title: "Delete note?", description: "This will permanently remove the note and its history.", confirmText: "Delete note" });
    if (!ok) return;
    await api.delete(`/notes/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="detail-notes">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">Notes & Progress Updates</h3>
        <Button size="sm" onClick={() => { setEditId(null); setForm({ title: "", content: "", client_visible: false }); setShowForm(true); }} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="detail-add-note"><Plus className="w-3 h-3 mr-1" /> Add Note</Button>
      </div>
      {showForm && (
        <div className="bg-card border border-border p-5 space-y-3">
          <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Note title" className="rounded-none bg-background border-border" data-testid="note-title-input" />
          <Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} placeholder="Note content..." rows={3} className="rounded-none bg-background border-border resize-none" data-testid="note-content-input" />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.client_visible} onChange={e => setForm({...form, client_visible: e.target.checked})} className="rounded" data-testid="note-visible-toggle" />
              <span>{form.client_visible ? <><Eye className="w-3 h-3 inline text-[#00CC52]" /> Visible to client</> : <><EyeOff className="w-3 h-3 inline text-muted-foreground" /> Internal only</>}</span>
            </label>
            <div className="flex gap-2">
              <Button size="sm" onClick={save} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="note-save"><Save className="w-3 h-3 mr-1" /> Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowForm(false)} className="rounded-none text-xs">Cancel</Button>
            </div>
          </div>
        </div>
      )}
      <div>
        <h4 className="font-bold text-sm mb-3 flex items-center gap-2"><EyeOff className="w-4 h-4" /> Internal Notes ({internal.length})</h4>
        {internal.length === 0 ? <p className="text-xs text-muted-foreground">No internal notes yet.</p> : (
          <div className="space-y-3">{internal.map(n => <NoteCard key={n.id} note={n} onEdit={() => startEdit(n)} onDelete={() => del(n.id)} fmtDate={fmtDate} />)}</div>
        )}
      </div>
      <div>
        <h4 className="font-bold text-sm mb-3 flex items-center gap-2"><Eye className="w-4 h-4 text-[#00CC52]" /> Client-Visible Progress Updates ({visible.length})</h4>
        {visible.length === 0 ? <p className="text-xs text-muted-foreground">No progress updates yet.</p> : (
          <div className="space-y-3">{visible.map(n => <NoteCard key={n.id} note={n} onEdit={() => startEdit(n)} onDelete={() => del(n.id)} fmtDate={fmtDate} />)}</div>
        )}
      </div>
    </div>
  );
}

function NoteCard({ note, onEdit, onDelete, fmtDate }) {
  return (
    <div className="bg-card border border-border p-4">
      <div className="flex items-start justify-between mb-2">
        <div><p className="font-bold text-sm">{note.title || "Untitled"}</p><p className="text-xs text-muted-foreground">{fmtDate(note.created_at)} &middot; {note.author}</p></div>
        <div className="flex gap-1">
          {note.client_visible && <Badge className="bg-[#00CC52]/10 text-[#00CC52] border-0 text-[10px]">Client Visible</Badge>}
          <Button size="sm" variant="ghost" onClick={onEdit} className="h-7 w-7 p-0"><Edit className="w-3 h-3" /></Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">{note.content}</p>
    </div>
  );
}

// ====== PROGRAMS TAB ======
function ProgramsTab({ clientId, programs, assignments, exercises, load, fmtDate }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", objective: "", start_date: "", end_date: "", duration_weeks: 8, baseline_assessment: "" });

  const createProgram = async () => {
    await api.post("/programs", { client_id: clientId, ...form });
    setForm({ title: "", objective: "", start_date: "", end_date: "", duration_weeks: 8, baseline_assessment: "" }); setShowForm(false); load();
  };
  const dup = async (pid) => { await api.post(`/programs/${pid}/duplicate`); load(); };
  const archiveP = async (pid) => { await api.put(`/programs/${pid}`, { status: "completed" }); load(); };
  const delP = async (pid) => {
    const p = programs.find(x => x.id === pid);
    const ok = await confirm({ title: "Delete program?", description: `This will permanently remove "${p?.title || "this program"}" and all its assignments. Assessments linked to it will be orphaned. This cannot be undone.`, confirmText: "Delete program" });
    if (!ok) return;
    await api.delete(`/programs/${pid}`); load();
  };
  const delA = async (aid) => {
    const ok = await confirm({ title: "Remove exercise from program?", description: "The exercise definition stays in the library — only this assignment is removed.", confirmText: "Remove" });
    if (!ok) return;
    await api.delete(`/assignments/${aid}`); load();
  };

  const [addExForm, setAddExForm] = useState(null);
  const addExercise = async (progId) => {
    if (!addExForm?.exercise_id) return;
    await api.post("/assignments", { client_id: clientId, exercise_id: addExForm.exercise_id, program_id: progId,
      day_of_week: addExForm.days?.split(",").map(d => d.trim()) || [], order: 1,
      custom_reps: addExForm.reps || "", custom_sets: addExForm.sets || "", custom_notes: addExForm.notes || "" });
    setAddExForm(null); load();
  };

  return (
    <div className="space-y-6" data-testid="detail-programs">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">Programs & Training</h3>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="detail-add-program"><Plus className="w-3 h-3 mr-1" /> New Program</Button>
      </div>
      {showForm && (
        <div className="bg-card border border-border p-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="program-title-input" /></div>
            <div><Label className="text-xs">Duration (weeks)</Label><Input type="number" value={form.duration_weeks} onChange={e => setForm({...form, duration_weeks: parseInt(e.target.value) || 0})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
          </div>
          <div><Label className="text-xs">Objective</Label><Textarea value={form.objective} onChange={e => setForm({...form, objective: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm resize-none" rows={2} /></div>
          <div><Label className="text-xs">Baseline Assessment</Label><Textarea value={form.baseline_assessment} onChange={e => setForm({...form, baseline_assessment: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm resize-none" rows={2} /></div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" onClick={createProgram} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="program-save"><Save className="w-3 h-3 mr-1" /> Create</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)} className="rounded-none text-xs">Cancel</Button>
          </div>
        </div>
      )}
      {programs.map(p => {
        const progAssignments = assignments.filter(a => a.program_id === p.id);
        return (
          <div key={p.id} className="bg-card border border-border p-5">
            <div className="flex items-start justify-between mb-3">
              <div><h4 className="font-bold">{p.title}</h4><p className="text-xs text-muted-foreground">{p.objective}</p></div>
              <div className="flex items-center gap-2">
                <Badge className={p.status === "in_progress" ? "bg-[#007AFF]/10 text-[#007AFF] border-0" : p.status === "draft" ? "bg-yellow-500/10 text-yellow-500 border-0" : ""}>{p.status?.replace("_", " ")}</Badge>
                <Button size="sm" variant="ghost" onClick={() => dup(p.id)} className="h-7 w-7 p-0" title="Duplicate"><Copy className="w-3 h-3" /></Button>
                {p.status === "in_progress" && <Button size="sm" variant="ghost" onClick={() => archiveP(p.id)} className="h-7 w-7 p-0" title="Archive"><CheckCircle className="w-3 h-3" /></Button>}
                <Button size="sm" variant="ghost" onClick={() => delP(p.id)} className="h-7 w-7 p-0 text-destructive" title="Delete"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>
            <div className="flex gap-4 text-xs text-muted-foreground mb-4"><span>{p.start_date} — {p.end_date}</span><span>{p.duration_weeks}w</span></div>
            {p.baseline_assessment && <p className="text-xs text-muted-foreground mb-4"><span className="font-bold">Baseline:</span> {p.baseline_assessment}</p>}
            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold">Assigned Exercises ({progAssignments.length})</p>
                <Button size="sm" variant="ghost" onClick={() => setAddExForm(addExForm?.progId === p.id ? null : { progId: p.id, exercise_id: "", reps: "", sets: "", days: "", notes: "" })} className="text-xs h-6 px-2"><Plus className="w-3 h-3 mr-1" /> Add</Button>
              </div>
              {addExForm?.progId === p.id && (
                <div className="bg-secondary/30 p-3 mb-3 space-y-2">
                  <Select value={addExForm.exercise_id} onValueChange={v => setAddExForm({...addExForm, exercise_id: v})}>
                    <SelectTrigger className="rounded-none bg-background border-border text-xs"><SelectValue placeholder="Select exercise..." /></SelectTrigger>
                    <SelectContent>{exercises.map(e => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="grid grid-cols-3 gap-2">
                    <Input placeholder="Reps" value={addExForm.reps} onChange={e => setAddExForm({...addExForm, reps: e.target.value})} className="rounded-none bg-background border-border text-xs" />
                    <Input placeholder="Sets" value={addExForm.sets} onChange={e => setAddExForm({...addExForm, sets: e.target.value})} className="rounded-none bg-background border-border text-xs" />
                    <Input placeholder="Days (mon,wed)" value={addExForm.days} onChange={e => setAddExForm({...addExForm, days: e.target.value})} className="rounded-none bg-background border-border text-xs" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" onClick={() => addExercise(p.id)} className="bg-[#007AFF] text-white rounded-none text-xs h-7">Add</Button>
                    <Button size="sm" variant="ghost" onClick={() => setAddExForm(null)} className="rounded-none text-xs h-7">Cancel</Button>
                  </div>
                </div>
              )}
              {progAssignments.map(a => (
                <div key={a.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-3">
                    {a.exercise?.thumbnail && <img src={a.exercise.thumbnail} alt="" className="w-10 h-10 object-cover" />}
                    <div><p className="text-sm font-medium">{a.exercise?.title || "Unknown"}</p><p className="text-xs text-muted-foreground">{a.custom_reps || a.exercise?.reps} x {a.custom_sets || a.exercise?.sets} &middot; {(a.day_of_week || []).join(", ")}</p></div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => delA(a.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3" /></Button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ====== SCHEDULE TAB ======
function ScheduleTab({ clientId, sessions, load, fmtDate }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ type: "in_person", date: "", time: "", duration_minutes: 60, notes: "" });
  const createSession = async () => {
    await api.post("/sessions", { client_id: clientId, ...form });
    setForm({ type: "in_person", date: "", time: "", duration_minutes: 60, notes: "" }); setShowForm(false); load();
  };
  const delS = async (sid) => {
    const s = sessions.find(x => x.id === sid);
    const when = s ? `${s.date} at ${s.time}` : "this session";
    const ok = await confirm({ title: "Cancel session?", description: `Remove the session on ${when}? The client will no longer see it on their schedule.`, confirmText: "Cancel session" });
    if (!ok) return;
    await api.delete(`/sessions/${sid}`); load();
  };
  const upcoming = sessions.filter(s => s.status === "scheduled");
  const past = sessions.filter(s => s.status !== "scheduled");

  return (
    <div className="space-y-6" data-testid="detail-schedule">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">Schedule</h3>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="detail-add-session"><Plus className="w-3 h-3 mr-1" /> Add Session</Button>
      </div>
      {showForm && (
        <div className="bg-card border border-border p-5 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Type</Label>
              <Select value={form.type} onValueChange={v => setForm({...form, type: v})}><SelectTrigger className="mt-1 rounded-none bg-background border-border text-xs"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="in_person">In Person</SelectItem><SelectItem value="virtual">Virtual</SelectItem><SelectItem value="evaluation">Evaluation</SelectItem><SelectItem value="check_in">Check-In</SelectItem></SelectContent></Select></div>
            <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="mt-1 rounded-none bg-background border-border text-xs" /></div>
            <div><Label className="text-xs">Time</Label><Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} className="mt-1 rounded-none bg-background border-border text-xs" /></div>
            <div><Label className="text-xs">Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: parseInt(e.target.value) || 60})} className="mt-1 rounded-none bg-background border-border text-xs" /></div>
          </div>
          <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 rounded-none bg-background border-border text-xs" /></div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" onClick={createSession} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs"><Save className="w-3 h-3 mr-1" /> Create</Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)} className="rounded-none text-xs">Cancel</Button>
          </div>
        </div>
      )}
      <ScheduleCalendar sessions={sessions} />
      <div>
        <h4 className="font-bold text-sm mb-3">Upcoming ({upcoming.length})</h4>
        {upcoming.map(s => (
          <div key={s.id} className="bg-card border border-border p-4 mb-2 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-[#007AFF]/10 flex items-center justify-center"><Calendar className="w-4 h-4 text-[#007AFF]" /></div>
              <div><p className="text-sm font-medium capitalize">{s.type?.replace("_", " ")}</p><p className="text-xs text-muted-foreground">{s.notes}</p></div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right"><p className="text-sm font-medium">{fmtDate(s.date)}</p><p className="text-xs text-muted-foreground">{s.time} ({s.duration_minutes}min)</p></div>
              <Button size="sm" variant="ghost" onClick={() => delS(s.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>
      {past.length > 0 && (
        <div>
          <h4 className="font-bold text-sm mb-3">Past ({past.length})</h4>
          {past.map(s => (
            <div key={s.id} className="bg-card border border-border p-4 mb-2 flex items-center justify-between opacity-60">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-secondary flex items-center justify-center"><Calendar className="w-4 h-4 text-muted-foreground" /></div>
                <div><p className="text-sm font-medium capitalize">{s.type?.replace("_", " ")}</p><p className="text-xs text-muted-foreground">{s.notes}</p></div>
              </div>
              <div className="text-right"><p className="text-sm font-medium">{fmtDate(s.date)}</p><Badge variant="secondary" className="text-[10px] mt-1">{s.status}</Badge></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ====== MILESTONES TAB ======
function MilestonesTab({ clientId, milestones, programs, load, fmtDate }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", date: "", program_id: "", achieved: false });
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.program_id) { toast.error("Please select the client program this milestone belongs to"); return; }
    try {
      if (editId) { await api.put(`/milestones/${editId}`, form); }
      else { await api.post("/milestones", { client_id: clientId, ...form }); }
      setForm({ title: "", description: "", date: "", program_id: "", achieved: false });
      setShowForm(false); setEditId(null); load();
      toast.success(editId ? "Milestone updated" : "Milestone added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save milestone");
    }
  };
  const del = async (id) => {
    const ok = await confirm({ title: "Delete milestone?", description: "The milestone and its achievement state will be permanently removed.", confirmText: "Delete milestone" });
    if (!ok) return;
    await api.delete(`/milestones/${id}`); load();
  };
  const toggle = async (m) => { await api.put(`/milestones/${m.id}`, { achieved: !m.achieved }); load(); };
  const progById = Object.fromEntries(programs.map(p => [p.id, p]));

  // Group milestones by program for clarity
  const byProgram = {};
  milestones.forEach(m => {
    const key = m.program_id || "unassigned";
    if (!byProgram[key]) byProgram[key] = [];
    byProgram[key].push(m);
  });

  return (
    <div className="space-y-6" data-testid="detail-milestones">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">Milestones</h3>
        <Button size="sm" onClick={() => {
          setEditId(null);
          const defaultProgram = programs.find(p => p.status === "in_progress")?.id || programs[0]?.id || "";
          setForm({ title: "", description: "", date: "", program_id: defaultProgram, achieved: false });
          setShowForm(true);
        }} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="detail-add-milestone">
          <Plus className="w-3 h-3 mr-1" /> Add Milestone
        </Button>
      </div>
      {programs.length === 0 && (
        <div className="bg-yellow-500/10 border-l-2 border-yellow-500 px-3 py-2 text-xs text-muted-foreground">
          This client has no programs yet. Create a program before adding milestones.
        </div>
      )}
      {showForm && (
        <div className="bg-card border border-border p-5 space-y-3">
          <div>
            <Label className="text-xs">Client Program <span className="text-destructive">*</span></Label>
            <Select value={form.program_id} onValueChange={v => setForm({...form, program_id: v})}>
              <SelectTrigger className="mt-1 rounded-none bg-background border-border text-sm" data-testid="milestone-program-select"><SelectValue placeholder="Select a program..." /></SelectTrigger>
              <SelectContent>
                {programs.map(p => <SelectItem key={p.id} value={p.id}>{p.title}{p.status === "in_progress" ? " (active)" : p.status === "completed" ? " (completed)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Title <span className="text-destructive">*</span></Label>
            <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Milestone title" className="mt-1 rounded-none bg-background border-border" data-testid="milestone-title-input" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What does this milestone look like when achieved?" rows={2} className="mt-1 rounded-none bg-background border-border resize-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Target Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={form.achieved ? "achieved" : "pending"} onValueChange={v => setForm({...form, achieved: v === "achieved"})}>
                <SelectTrigger className="mt-1 rounded-none bg-background border-border text-sm"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="pending">Pending</SelectItem><SelectItem value="achieved">Achieved</SelectItem></SelectContent></Select></div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" onClick={save} disabled={!form.program_id || !form.title.trim()} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="milestone-save"><Save className="w-3 h-3 mr-1" /> Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditId(null); }} className="rounded-none text-xs">Cancel</Button>
          </div>
        </div>
      )}
      <div className="space-y-6">
        {Object.keys(byProgram).length === 0 && !showForm && <p className="text-xs text-muted-foreground">No milestones yet.</p>}
        {Object.entries(byProgram).map(([pid, items]) => {
          const prog = progById[pid];
          const label = prog ? `${prog.title}${prog.status === "in_progress" ? " · Active" : ""}` : "Unassigned (legacy)";
          return (
            <div key={pid}>
              <div className="flex items-baseline justify-between border-b border-border pb-1 mb-2">
                <h4 className="font-bold text-sm">{label}</h4>
                <span className="text-[11px] text-muted-foreground">{items.filter(x => x.achieved).length} / {items.length} achieved</span>
              </div>
              <div className="space-y-3">
                {items.map(m => (
                  <div key={m.id} className="bg-card border border-border p-4 flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <button onClick={() => toggle(m)} className={`w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5 ${m.achieved ? "bg-[#00CC52]/10" : "bg-secondary"}`}>
                        <CheckCircle className={`w-3.5 h-3.5 ${m.achieved ? "text-[#00CC52]" : "text-muted-foreground"}`} />
                      </button>
                      <div>
                        <p className={`font-bold text-sm ${m.achieved ? "" : "text-muted-foreground"}`}>{m.title}</p>
                        {m.description && <p className="text-xs text-muted-foreground mt-1">{m.description}</p>}
                        {m.date && <p className="text-xs text-muted-foreground mt-1">Target: {fmtDate(m.date)}</p>}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => { setForm({ title: m.title, description: m.description || "", date: m.date || "", program_id: m.program_id || "", achieved: m.achieved }); setEditId(m.id); setShowForm(true); }} className="h-7 w-7 p-0"><Edit className="w-3 h-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(m.id)} className="h-7 w-7 p-0 text-destructive"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ====== DOCUMENTS TAB ======
function DocumentsTab({ clientId, documents, load, fmtDate }) {
  const [showForm, setShowForm] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ name: "", client_visible: false, tags: "" });
  const [uploading, setUploading] = useState(0); // 0-100
  const fileInputRef = { current: null };

  const reset = () => { setForm({ name: "", client_visible: false, tags: "" }); setFile(null); setShowForm(false); setUploading(0); };

  const upload = async () => {
    if (!file) { toast.error("Please choose a file"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("client_id", clientId);
    fd.append("name", form.name);
    fd.append("tags", form.tags);
    fd.append("client_visible", form.client_visible ? "true" : "false");
    try {
      setUploading(1);
      await api.post("/documents/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (ev) => {
          if (ev.total) setUploading(Math.max(1, Math.round((ev.loaded / ev.total) * 100)));
        },
      });
      toast.success("Document uploaded");
      reset(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      setUploading(0);
    }
  };

  const del = async (id) => {
    const d = documents.find(x => x.id === id);
    const ok = await confirm({ title: "Delete document?", description: `"${d?.name || "This file"}" will be permanently deleted from storage.`, confirmText: "Delete document" });
    if (!ok) return;
    await api.delete(`/documents/${id}`); load();
  };
  const toggleVis = async (d) => { await api.put(`/documents/${d.id}`, { client_visible: !d.client_visible }); load(); };
  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || d.name || "download"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };
  const internal = documents.filter(d => !d.client_visible);
  const visible = documents.filter(d => d.client_visible);

  return (
    <div className="space-y-6" data-testid="detail-documents">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">Documents</h3>
        <Button size="sm" onClick={() => setShowForm(true)} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="detail-add-document"><Upload className="w-3 h-3 mr-1" /> Upload Document</Button>
      </div>
      {showForm && (
        <div className="bg-card border border-border p-5 space-y-3" data-testid="doc-upload-form">
          <div>
            <Label className="text-xs">File (max 25MB)</Label>
            <input
              ref={(el) => { fileInputRef.current = el; }}
              type="file"
              onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); if (f && !form.name) setForm(v => ({...v, name: f.name})); }}
              className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#007AFF] file:text-white file:font-bold file:text-xs hover:file:bg-[#3395FF] bg-background border border-border p-2"
              data-testid="doc-file-input"
            />
            {file && <p className="text-xs text-muted-foreground mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</p>}
          </div>
          <div><Label className="text-xs">Display Name</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Baseline Assessment — Apr 2026" className="mt-1 rounded-none bg-background border-border text-sm" data-testid="doc-name-input" /></div>
          <Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="Tags (comma separated)" className="rounded-none bg-background border-border text-sm" data-testid="doc-tags-input" />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={form.client_visible} onChange={e => setForm({...form, client_visible: e.target.checked})} className="rounded" data-testid="doc-visible-checkbox" />
              <span>{form.client_visible ? <><Eye className="w-3 h-3 inline text-[#00CC52]" /> Client visible</> : <><EyeOff className="w-3 h-3 inline" /> Internal only</>}</span>
            </label>
            <div className="flex gap-2 items-center">
              {uploading > 0 && uploading < 100 && <span className="text-xs text-muted-foreground">{uploading}%</span>}
              <Button size="sm" onClick={upload} disabled={!file || uploading > 0} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="doc-upload-submit"><Save className="w-3 h-3 mr-1" /> {uploading > 0 ? "Uploading..." : "Upload"}</Button>
              <Button size="sm" variant="ghost" onClick={reset} className="rounded-none text-xs">Cancel</Button>
            </div>
          </div>
        </div>
      )}
      <div>
        <h4 className="font-bold text-sm mb-3"><Eye className="w-4 h-4 inline text-[#00CC52] mr-1" /> Client-Visible ({visible.length})</h4>
        {visible.length === 0 ? <p className="text-xs text-muted-foreground">None</p> : visible.map(d => <DocRow key={d.id} doc={d} onDelete={() => del(d.id)} onToggle={() => toggleVis(d)} onDownload={() => download(d)} fmtDate={fmtDate} />)}
      </div>
      <div>
        <h4 className="font-bold text-sm mb-3"><EyeOff className="w-4 h-4 inline mr-1" /> Internal Only ({internal.length})</h4>
        {internal.length === 0 ? <p className="text-xs text-muted-foreground">None</p> : internal.map(d => <DocRow key={d.id} doc={d} onDelete={() => del(d.id)} onToggle={() => toggleVis(d)} onDownload={() => download(d)} fmtDate={fmtDate} />)}
      </div>
    </div>
  );
}

function DocRow({ doc, onDelete, onToggle, onDownload, fmtDate }) {
  const hasFile = !!doc.stored_filename;
  const sizeKb = doc.size_bytes ? `${(doc.size_bytes / 1024).toFixed(1)} KB` : null;
  return (
    <div className="bg-card border border-border p-3 mb-2 flex items-center justify-between" data-testid={`doc-row-${doc.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 bg-secondary flex items-center justify-center text-xs font-bold uppercase flex-shrink-0">{doc.file_type}</div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{doc.name}</p>
          <div className="flex gap-2 flex-wrap text-xs text-muted-foreground items-center">
            <span>{fmtDate(doc.created_at)}</span>
            {sizeKb && <span>· {sizeKb}</span>}
            {doc.tags?.map(t => <Badge key={t} variant="outline" className="text-[10px] px-1">{t}</Badge>)}
          </div>
        </div>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        {hasFile && <Button size="sm" variant="ghost" onClick={onDownload} className="h-7 w-7 p-0" title="Download" data-testid={`doc-download-${doc.id}`}><Download className="w-3 h-3" /></Button>}
        <Button size="sm" variant="ghost" onClick={onToggle} className="h-7 w-7 p-0" title="Toggle visibility">{doc.client_visible ? <Eye className="w-3 h-3 text-[#00CC52]" /> : <EyeOff className="w-3 h-3" />}</Button>
        <Button size="sm" variant="ghost" onClick={onDelete} className="h-7 w-7 p-0 text-destructive" data-testid={`doc-delete-${doc.id}`}><Trash2 className="w-3 h-3" /></Button>
      </div>
    </div>
  );
}
